import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text } from '../elements/text.element';
import { Icon } from '../elements/icon.element';
import { identity } from '../../api/identity';
import { colorForRung, displayLevel } from '../../utils/levelPolicy.js';
import { colorForCustomerLevel, labelForCustomerLevel } from '../../utils/customerLevelPolicy.js';
import { PersonaSwitcher } from './persona-switcher.block.jsx';
import { StaffViewAs } from './staff-view-as.block.jsx';
import { PERSONA_HOME } from '@moksy/common/src/permissions/personas.js';
import { OWNER_ROLE_SET } from '@moksy/common/src/permissions/ownerRoles.js';
// The capability table decides what a role may do; the header should not keep
// a second opinion about it (GC-126).
import { can } from '@moksy/common/src/permissions/can.js';
import { greetingName, fullName, nameWithHandle } from '@moksy/common/src/identity/displayName.js';

/**
 * Human-readable display labels for each role key. Kept in sync with
 * the UserRoles enum in foundation/users/src/models/User.js — the
 * full owner-group + user-group set is represented so the badge
 * never falls back to a raw machine string like "super_admin".
 *
 * Note disambiguations vs owner-group:
 *   finance_manager  ≠ finance  (customer's role vs Moksy operator)
 *   tech_support     ≠ support  ( "" )
 *   internal_admin   ≠ admin    ( "" )
 */
const ROLE_LABELS = {
  // Legacy / generic user-track
  user: 'Member',
  developer: 'Developer',
  designer: 'Designer',
  content_writer: 'Content Writer',
  reviewer: 'Reviewer',
  // Leadership
  ceo: 'CEO',
  cto: 'CTO',
  // Product & delivery
  product_manager: 'Product Manager',
  delivery_manager: 'Delivery Manager',
  project_manager: 'Project Manager',
  team_lead: 'Team Lead',
  user_researcher: 'User Researcher',
  // Engineering
  software_engineer: 'Software Engineer',
  backend_developer: 'Backend Developer',
  frontend_developer: 'Frontend Developer',
  fullstack_developer: 'Full-Stack Developer',
  devops_engineer: 'DevOps Engineer',
  security_expert: 'Security Expert',
  qa_tester: 'QA Tester',
  // Design
  graphics_designer: 'Graphics Designer',
  // Business & ops
  account_manager: 'Account Manager',
  sales_manager: 'Sales Manager',
  marketing_manager: 'Marketing Manager',
  social_media_manager: 'Social Media Manager',
  finance_manager: 'Finance Manager',
  compliance_officer: 'Compliance Officer',
  tech_support: 'Tech Support',
  internal_admin: 'Internal Admin',
  // ─── Owner-group (Moksy staff) ───────────────────────────────
  // Tier 5 — Front-line
  content_moderator:        'Content Moderator',
  support:                  'Support',
  // Tier 4 — Specialists
  support_lead:             'Support Lead',
  reviewer_coordinator:     'Reviewer Coordinator',
  release_manager:          'Release Manager',
  incident_responder:       'Incident Responder',
  // Tier 3 — Department operators
  operations_manager:       'Operations Manager',
  finance_controller:       'Finance Controller',
  finance:                  'Finance',
  data_protection_officer:  'Data Protection Officer',
  manager:                  'Manager',
  auditor:                  'Auditor',
  // Tier 2 — Senior leadership
  head_of_support:          'Head of Support',
  head_of_platform:         'Head of Platform',
  admin:                    'Admin',
  // Tier 1 — Executive
  chief_security_officer:   'CSO',
  chief_financial_officer:  'CFO',
  chief_compliance_officer: 'CCO',
  chief_operating_officer:  'COO',
  super_admin:              'Super Admin'
};

/**
 * Colour accents per role — used on the active role badge trigger.
 * Categories share a palette so a glance tells you what kind of role
 * is active: leadership = purple, eng = green/blue, ops = emerald,
 * design = pink, sales/marketing = rose, owner-group = warm.
 */
const ROLE_COLORS = {
  // Generic user-track
  user:           'bg-blue-50 text-blue-700 border-blue-200',
  developer:      'bg-green-50 text-green-700 border-green-200',
  designer:       'bg-pink-50 text-pink-700 border-pink-200',
  content_writer: 'bg-amber-50 text-amber-700 border-amber-200',
  reviewer:       'bg-indigo-50 text-indigo-700 border-indigo-200',
  // Leadership
  ceo:            'bg-purple-50 text-purple-700 border-purple-200',
  cto:            'bg-purple-50 text-purple-700 border-purple-200',
  // Product / delivery
  product_manager:  'bg-orange-50 text-orange-700 border-orange-200',
  delivery_manager: 'bg-orange-50 text-orange-700 border-orange-200',
  project_manager:  'bg-orange-50 text-orange-700 border-orange-200',
  team_lead:        'bg-orange-50 text-orange-700 border-orange-200',
  user_researcher:  'bg-cyan-50 text-cyan-700 border-cyan-200',
  // Engineering
  software_engineer:  'bg-green-50 text-green-700 border-green-200',
  backend_developer:  'bg-green-50 text-green-700 border-green-200',
  frontend_developer: 'bg-green-50 text-green-700 border-green-200',
  fullstack_developer:'bg-green-50 text-green-700 border-green-200',
  devops_engineer:    'bg-teal-50 text-teal-700 border-teal-200',
  security_expert:    'bg-slate-50 text-slate-700 border-slate-200',
  qa_tester:          'bg-lime-50 text-lime-700 border-lime-200',
  // Design
  graphics_designer:  'bg-pink-50 text-pink-700 border-pink-200',
  // Business & ops
  account_manager:      'bg-rose-50 text-rose-700 border-rose-200',
  sales_manager:        'bg-rose-50 text-rose-700 border-rose-200',
  marketing_manager:    'bg-rose-50 text-rose-700 border-rose-200',
  social_media_manager: 'bg-rose-50 text-rose-700 border-rose-200',
  finance_manager:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  compliance_officer:   'bg-stone-50 text-stone-700 border-stone-200',
  tech_support:         'bg-sky-50 text-sky-700 border-sky-200',
  internal_admin:       'bg-gray-50 text-gray-700 border-gray-200',
  // ─── Owner-group (Moksy staff) ───────────────────────────────
  // Palette philosophy: front-line/specialists get cool palette
  // (sky/teal/indigo); department ops get warm (emerald/orange/amber);
  // senior leadership gets red; executive gets purple. Higher rank
  // = more saturated treatment.
  content_moderator:        'bg-sky-50 text-sky-700 border-sky-200',
  support:                  'bg-sky-50 text-sky-700 border-sky-200',
  support_lead:             'bg-sky-100 text-sky-800 border-sky-300',
  reviewer_coordinator:     'bg-teal-100 text-teal-800 border-teal-300',
  release_manager:          'bg-indigo-100 text-indigo-800 border-indigo-300',
  incident_responder:       'bg-rose-100 text-rose-800 border-rose-300',
  operations_manager:       'bg-orange-100 text-orange-800 border-orange-300',
  finance_controller:       'bg-emerald-100 text-emerald-800 border-emerald-300',
  finance:                  'bg-emerald-100 text-emerald-800 border-emerald-300',
  data_protection_officer:  'bg-stone-100 text-stone-800 border-stone-300',
  manager:                  'bg-orange-100 text-orange-800 border-orange-300',
  auditor:                  'bg-slate-100 text-slate-800 border-slate-300',
  head_of_support:          'bg-red-50 text-red-700 border-red-200',
  head_of_platform:         'bg-red-50 text-red-700 border-red-200',
  admin:                    'bg-red-100 text-red-800 border-red-300',
  chief_security_officer:   'bg-purple-50 text-purple-700 border-purple-200',
  chief_financial_officer:  'bg-purple-50 text-purple-700 border-purple-200',
  chief_compliance_officer: 'bg-purple-50 text-purple-700 border-purple-200',
  chief_operating_officer:  'bg-purple-100 text-purple-800 border-purple-300',
  super_admin:              'bg-purple-200 text-purple-900 border-purple-400'
};

// Owner-group roles, imported rather than mirrored. Used here to suppress
// customer-track menu items (Purchases, Subscription) for operators whose
// staff account has no plan or wallet to manage.
const OWNER_ROLES = OWNER_ROLE_SET;

/**
 * 🔑 WHICH POWERS AM I HOLDING RIGHT NOW? (GC-126)
 *
 * Walked as staff 2026-09-12: an admin and a super admin were told the same
 * thing by this header — "Hi Mokter", and a coloured word. Two accounts that
 * differ on whether you can change role definitions, read finance data or
 * import into a collection looked identical at a glance, on the one control
 * that is meant to tell you whose hat you are wearing. The badge already
 * names the ROLE; what was missing is what the role means.
 *
 * Asked of the capability table rather than restated here, so a role whose
 * powers change does not leave a sentence behind claiming otherwise. Three
 * probes, chosen because they are the ones that separate the owner roles from
 * each other in practice.
 */
const STAFF_POWER_PROBES = [
  { id: 'admin.role_definitions.update', label: 'change role definitions' },
  { id: 'reports.finance.read',          label: 'read finance data' },
  { id: 'reports.data.export',           label: 'export platform data' }
];

// The two-person rule is enforced on the request, not here, so a capability
// that only fails for want of a second approver still counts as held.
const holdsPower = (user, id) => {
  const verdict = can(user, id, { twoPersonRule: { verified: true, verifiedAt: Date.now() } });
  return verdict.ok;
};

const getRoleLabel = (role) => ROLE_LABELS[role] || role;
const getRoleColor = (role) => ROLE_COLORS[role] || 'bg-gray-50 text-gray-700 border-gray-200';
const normalizeRole = (role) => String(role || '').trim().toLowerCase();

export const IdentityMenu = ({
  user, logout, lang, activeRole, availableRoles, switchRole,
  personaOptions = [], activePersona, switchPersona, rolesInPersona = [],
  canPreviewAsGuest = false, startGuestPreview
}) => {
  const nav = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [myOrgs, setMyOrgs] = useState([]);

  const avatarSrc = user?.about?.photoUrl || user?.profilePictureUrl || user?.avatarUrl || user?.pictureUrl || null;
  const avatarIcon = user?.avatarIcon || user?.about?.avatarIcon || null;
  // 👤 One shared rule — see packages/common/src/identity/displayName.
  //
  // This chain ended at `user?.email`, so an account with no first name
  // and no username was greeted by its email address on a screen the
  // person might be sharing. It fell through to the USERNAME first, which
  // is why a signed-in dashboard said "Hi azizmeha".
  const firstName = greetingName(user);

  const primaryRole = normalizeRole(user?.role) || 'user';
  const displayRole = Array.isArray(availableRoles) && availableRoles.includes(activeRole)
    ? normalizeRole(activeRole)
    : primaryRole;
  // The chevron on the badge means "there is somewhere else to go". Personas
  // decide that now, not the raw role count — the old test
  // (`availableRoles.length > 1`) hid the control from a super admin, who
  // holds exactly one role and is the person most likely to want it.
  const showRoleSwitcher = Array.isArray(personaOptions)
    && personaOptions.filter((o) => o.available).length > 1;
  const primaryOrg = Array.isArray(myOrgs) && myOrgs.length > 0 ? myOrgs[0] : null;
  const orgMenuItem = primaryOrg
    ? { id: 'company', label: 'My Company', icon: 'vault', to: `/orgs/${primaryOrg.orgId || primaryOrg._id || primaryOrg.id}` }
    : { id: 'company', label: 'My Company', icon: 'vault', to: '/orgs' };

  useEffect(() => {
    let mounted = true;

    const loadOrgs = async () => {
      try {
        const items = await identity.listMyOrgs();
        if (!mounted) return;
        setMyOrgs(Array.isArray(items) ? items : []);
      } catch {
        if (mounted) setMyOrgs([]);
      }
    };

    loadOrgs();
    return () => { mounted = false; };
  }, []);

  // Customer-track items disappear for owner-group operators — their
  // staff account doesn't carry a plan, wallet, or purchase history,
  // so the surfaces would just empty-state. Operators get a
  // streamlined menu pointing at their own profile + identity
  // console; everything else lives in the sidebar.
  const isOwnerGroup = OWNER_ROLES.has(displayRole);

  // 🎖️ Novice → Titan is the REVIEWER-track ladder (the LVL scale that
  // decides when a member is nominated toward accreditation). It was
  // rendered for everybody, so a customer who has never applied to
  // review saw "Member · Novice" in their header and read it as a rank
  // the platform had given them — on a scale that means nothing to
  // somebody who is not reviewing.
  //
  // Shown only in reviewer context: either they are acting as a reviewer
  // now, or reviewer is one of the roles they hold (so a reviewer who has
  // switched to their customer hat is not told their level twice).
  // Operators see it because staff read it about the people they manage.
  //
  // Customers get `customerLevel` instead, just below — a scale about
  // their relationship with us, which is the thing that applies to them.
  const isReviewerContext = displayRole === 'reviewer'
    || (Array.isArray(availableRoles) && availableRoles.map(normalizeRole).includes('reviewer'))
    || isOwnerGroup;
  const baseMenuItems = isOwnerGroup
    ? [
        { id: 'projects', label: 'Projects', icon: 'projects', to: '/projects' },
        { id: 'identity', label: 'User registry', icon: 'profile', to: '/admin/identity' },
        { id: 'profile', label: 'Profile', icon: 'profile', to: '/profile' },
        { id: 'developer', label: 'API & webhooks', icon: 'open', to: '/settings/developer' },
        { id: 'settings', label: 'Settings', icon: 'settings', to: '/profile' }
      ]
    : [
        { id: 'projects', label: 'Projects', icon: 'projects', to: '/projects' },
        // 'payments' is not a registry glyph; every member saw the fallback
        // circle beside "Purchases".
        { id: 'purchases', label: 'Purchases', icon: 'creditCard', to: '/purchases' },
        { id: 'subscriptions', label: 'Membership', icon: 'subscriptions', to: '/billing?tab=subscription' },
        { id: 'profile', label: 'Profile', icon: 'profile', to: '/profile' },
        { id: 'mydata', label: 'Your data & privacy', icon: 'shield', to: '/settings/my-data' },
        { id: 'developer', label: 'API & webhooks', icon: 'open', to: '/settings/developer' },
        { id: 'settings', label: 'Settings', icon: 'settings', to: '/profile' }
      ];
  // The org entry sits right after "My Projects" when present. Skip
  // for owner-group — Moksy staff don't own orgs through their staff
  // account; if they happen to, they'd manage them via a separate
  // customer-track session.
  const menuItems = !isOwnerGroup && myOrgs.length > 0
    ? [baseMenuItems[0], orgMenuItem, ...baseMenuItems.slice(1)]
    : baseMenuItems;

  const handleClose = () => setIsOpen(false);

  /**
   * Switching a hat also moves you to that hat's home.
   *
   * Without the navigation the switch is half-done: the sidebar re-filters but
   * you are still standing on a page the new persona does not have, and the
   * route guard decides where you end up. Navigating only on a SUCCESSFUL
   * switch matters too — `switchPersona` returns false for a persona the
   * account does not hold, and moving anyway would take somebody somewhere
   * they did not ask to go on the strength of a refused action.
   */
  const handlePersonaSwitch = (persona) => {
    if (switchPersona?.(persona) === false) return;
    const home = PERSONA_HOME[persona];
    if (home) nav(home);
  };

  return (
    <div className="relative">
      {/* ── Trigger button ─────────────────────────────────────── */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-1 pr-3 rounded-full hover:bg-gray-50 transition-all border border-transparent hover:border-gray-100"
        // The role travels with the greeting, including on the narrow layout
        // where the whole text block is hidden — an operator should never have
        // to guess which hat the header is showing (GC-126).
        title={`${firstName} · ${getRoleLabel(displayRole)}`}
        // (The label stays exactly "Open account menu": the role is already
        // read out by the badge inside this button, so repeating it here
        // would only make the control's name longer than its purpose.)
        aria-label="Open account menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={user?.name || user?.username || 'Your account'}
            className="w-8 h-8 rounded-full object-cover border border-gray-200 shadow-inner"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-neutral flex items-center justify-center text-white text-xs font-bold uppercase shadow-inner">
            {avatarIcon || user?.username?.charAt(0) || 'U'}
          </div>
        )}
        <div className="text-left hidden sm:block">
          {/* ♿ `as="span"`, because a greeting is not a section heading.
              `variant` is the type scale and `as` is the element — the
              primitive says so — and this used the h2 variant for its weight
              while emitting a real <h2>. Being in the shared header, it made
              "Hi <name>" the FIRST heading on every page in the product, above
              each page's own <h1>: a heading-order violation platform-wide,
              and a screen-reader outline that opens with a greeting instead of
              saying where you are. The className already overrides the size,
              so nothing moves visually. */}
          <Text as="span" variant="h2" className="text-xs block leading-none">{`Hi ${firstName}`}</Text>
          {/* Active role + level badges. Role decides what they can
              do (security ceiling); level decides what they've earned
              (quality / seniority). Both render side-by-side so the
              user can see both at a glance. */}
          <span className="mt-1 inline-flex items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${getRoleColor(displayRole)}`}
            >
              {getRoleLabel(displayRole)}
              {showRoleSwitcher && (
                <svg className="w-2.5 h-2.5 opacity-60" fill="none" viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            {isReviewerContext && user?.level?.current ? (
              <span
                title={`Level ${user.level.score ?? '—'} / 100${user.level.manuallyPinnedTo ? ' (pinned by admin)' : ''}`}
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${colorForRung(user.level.current)}`}
              >
                {user.level.manuallyPinnedTo ? '📌 ' : ''}{displayLevel(user.level.current, { compact: true })}
              </span>
            ) : null}
            {/* 🎯 Customer-level pill — relationship classification.
                Distinct from reviewer level. Owner-group operators
                skip this (their staff account doesn't transact).

                The `free` and `guest` rungs are deliberately NOT rendered.
                They are the default state, so they carry no signal — and
                because this scale shares its vocabulary with the billing
                plan (both have a "Free" and a "Paid"), showing the free
                rung put the word "Free" in the header of customers who
                had just paid for a plan. Two different axes, one word:
                the pill is only worth the space once it disagrees with
                the default. */}
            {user?.customerLevel?.current
              && !['free', 'guest'].includes(String(user.customerLevel.current).toLowerCase())
              && String(user?.group || '').toLowerCase() !== 'owner' ? (
              <span
                title={
                  `Customer · ${labelForCustomerLevel(user.customerLevel.current)}` +
                  (user.customerLevel.manuallyPinnedTo ? ' (pinned by admin)' : '') +
                  (user.customerLevel.signals?.lifetimeSpendUsd
                    ? ` · lifetime spend $${Number(user.customerLevel.signals.lifetimeSpendUsd).toLocaleString()}`
                    : '')
                }
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${colorForCustomerLevel(user.customerLevel.current)}`}
              >
                {user.customerLevel.manuallyPinnedTo ? '📌 ' : ''}{labelForCustomerLevel(user.customerLevel.current)}
              </span>
            ) : null}
          </span>
        </div>
      </button>

      {/* ── Dropdown panel ─────────────────────────────────────── */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-0" onClick={handleClose} aria-hidden="true" />

          <div
            className="absolute right-0 mt-2 w-72 bg-background border border-gray-200 rounded-2xl shadow-2xl py-2 z-10 animate-in zoom-in-95 duration-100"
            role="menu"
            aria-label="Account menu"
          >
            {/* Signed-in identity */}
            <div className="px-4 py-2 border-b border-gray-100 mb-1">
              <Text variant="label" className="mb-0.5">Signed in as</Text>
              <Text variant="body" className="font-bold truncate block">{user?.email}</Text>
              {/* 🔑 What this account can do, for staff (GC-126). Admin and
                  super admin were indistinguishable here, so an operator could
                  not tell whether the action they were about to attempt was
                  one of theirs until the server refused it. */}
              {isOwnerGroup ? (
                <div className="mt-1.5">
                  <Text variant="label" className="mb-0.5">Powers you hold</Text>
                  <ul className="space-y-0.5">
                    {STAFF_POWER_PROBES.map((probe) => {
                      const held = holdsPower({ ...(user || {}), role: displayRole }, probe.id);
                      return (
                        <li key={probe.id} className="flex items-start gap-1.5 text-[11px] text-gray-600">
                          <span aria-hidden="true" className={held ? 'text-emerald-600' : 'text-gray-400'}>
                            {held ? '✓' : '—'}
                          </span>
                          <span className={held ? '' : 'text-gray-400'}>
                            {held ? 'Can' : 'Cannot'} {probe.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>

            {/* ── Working as: your own identity, a different hat ── */}
            <PersonaSwitcher
              options={personaOptions}
              activePersona={activePersona}
              onSwitch={handlePersonaSwitch}
              rolesInPersona={rolesInPersona}
              activeRole={displayRole}
              onSwitchRole={switchRole}
              roleLabel={getRoleLabel}
              onDone={handleClose}
            />

            {/* ── View as: staff tools, deliberately a separate section ── */}
            <StaffViewAs
              canPreviewAsGuest={canPreviewAsGuest}
              onGuestPreview={startGuestPreview}
              onDone={handleClose}
            />

            {/* ── Navigation items ── */}
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { handleClose(); nav(item.to); }}
                role="menuitem"
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
              >
                <Icon name={item.icon} className="w-4 h-4" />
                {item.label}
              </button>
            ))}

            {/* ── Logout ── */}
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={async () => {
                  if (isLoggingOut) return;
                  setIsLoggingOut(true);
                  handleClose();
                  try { await logout(); } finally { setIsLoggingOut(false); }
                }}
                disabled={isLoggingOut}
                role="menuitem"
                className="w-full text-left px-4 py-2 text-sm text-red-600 font-bold hover:bg-red-50 transition-colors inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Icon name="logout" className="w-4 h-4" />
                {isLoggingOut ? 'Logging out…' : 'Logout'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
